import { RuntimeChatUploadClient } from "./RuntimeChatUploadClient";

export type CoreChatOptions = { sessionKey?: string; signal?: AbortSignal };
export type CoreChatOpenRequest = { agentId: string; conversationId?: string; forceNew?: boolean; requestId?: string };
export type CoreChatMessageRequest = { message?: string; fileIds?: readonly string[]; requestId?: string };

/** Host-bound chat with agent selection at open and server-enforced session ownership and policies. */
export class ReactChatClient extends RuntimeChatUploadClient {
  forHost(definitionId: string, componentId: string, preview = false, canUse?: () => boolean) {
    const allow = () => {
      if (preview || (canUse && !canUse())) throw new Error("Managed chat is read-only in designer previews. Sign in to the runtime to test it here.");
    };
    return {
      openSession: async (request: CoreChatOpenRequest, options: CoreChatOptions = {}) => {
        allow();
        return this.open(definitionId, componentId, options.sessionKey, { ...request, forceNew: request.forceNew ?? false }, options.signal);
      },
      listSessions: async (pageIndex = 0, options: CoreChatOptions = {}) => {
        allow();
        return this.sessions(definitionId, componentId, options.sessionKey, pageIndex, options.signal);
      },
      getSession: async (id: string, options: CoreChatOptions = {}) => {
        allow();
        return this.get(definitionId, componentId, id, options.sessionKey, options.signal);
      },
      getUpdates: async (id: string, afterSequence: number, options: CoreChatOptions = {}) => {
        allow();
        return this.updates(definitionId, componentId, id, options.sessionKey, afterSequence, options.signal);
      },
      getMessages: async (id: string, beforeSequence?: number, options: CoreChatOptions = {}) => {
        allow();
        return this.before(definitionId, componentId, id, options.sessionKey, beforeSequence, options.signal);
      },
      sendMessage: async (id: string, request: CoreChatMessageRequest, options: CoreChatOptions = {}) => {
        allow();
        return this.send(definitionId, componentId, id, options.sessionKey, { ...request, fileIds: request.fileIds?.slice() }, options.signal);
      },
      uploadAttachment: async (id: string, file: File, options: CoreChatOptions = {}) => {
        allow();
        return this.uploadFile(definitionId, componentId, id, options.sessionKey, file, options.signal);
      },
      deleteDraftAttachment: async (id: string, fileId: string, options: CoreChatOptions = {}) => {
        allow();
        return this.deleteDraft(definitionId, componentId, id, fileId, options.sessionKey, options.signal);
      },
      previewAttachment: async (id: string, fileId: string, options: CoreChatOptions = {}) => {
        allow();
        return this.preview(definitionId, componentId, id, fileId, options.sessionKey, options.signal);
      },
      downloadAttachment: async (id: string, fileId: string, options: CoreChatOptions = {}) => {
        allow();
        return this.download(definitionId, componentId, id, fileId, options.sessionKey, options.signal);
      },
    };
  }
}
